import type { ApiClientErrorCode, DecorKind, DecorListItem } from '@pomoshnik/shared';
import { Header } from '@/components/Header/Header';
import { BottomBar } from '@/components/BottomBar/BottomBar';
import { DecorResultCard } from './DecorResultCard';

/**
 * Presenter Экрана 2.2.2-search «Найти декор по названию» (ADR-0067). См. ADR-0005.
 *
 * Чистая функция props → JSX. Никаких useEffect / fetch / useNavigate / debounce —
 * всё это в Container (DecorsSearch). 5 состояний (P-021): idle / loading /
 * success / empty / error.
 *
 * P-020: в поле ввода НЕТ переключателя языка (он только в верхнем меню). Поле —
 * обычный `<input type="text">` с видимой `<label>`.
 *
 * P-040: VideoButton тут НЕ ставим — это дочерний экран метод-пикера (2.2.2),
 * VideoButton живёт на Block 2 KindPicker (Экран 2.2.1). Дублирование запрещено.
 *
 * P-019: результаты — вертикальный список карточек (DecorResultCard, как каталог).
 */
export type DecorsSearchState = 'idle' | 'loading' | 'success' | 'empty' | 'error';

export interface DecorsSearchViewProps {
  kind: DecorKind;
  state: DecorsSearchState;
  /** Текущее значение поля (controlled). */
  query: string;
  onQueryChange: (next: string) => void;

  /** Только при state='success' / 'empty'. */
  items?: readonly DecorListItem[];
  /** Cursor следующей страницы. null → нет данных или достигнут конец. */
  nextCursor: string | null;
  /** Только при state='error'. */
  error?: { code: ApiClientErrorCode; message: string };

  /** Фото-2 (PR-DATA4): signed-URL по `primaryPhotoKey` (из useDecorImageUrls). */
  imageUrls?: Record<string, string>;

  onLoadMore: () => void;
  onItemClick: (globalId: string) => void;
}

// Заголовок экрана повторяет каталог (DecorsCatalogView.titleForKind) — даёт
// пользователю контекст kind, внутри которого идёт поиск (поиск ВНУТРИ фильтра).
function titleForKind(kind: DecorKind): string {
  switch (kind) {
    case 'facade':
      return 'Фасады';
    case 'countertop':
      return 'Столешницы';
    case 'panel':
      return 'Корпус ЛДСП';
  }
}

export function DecorsSearchView(props: DecorsSearchViewProps) {
  const title = titleForKind(props.kind);

  return (
    <main className="min-h-screen flex flex-col bg-canvas">
      <Header title={title} />

      {/* Поле ввода — всегда видно (не в скролл-зоне). */}
      <section className="border-b border-icon-border p-6 flex flex-col gap-2">
        <label htmlFor="decor-name-search" className="text-ink text-base font-body font-semibold">
          Название декора
        </label>
        <input
          id="decor-name-search"
          type="text"
          inputMode="search"
          autoComplete="off"
          value={props.query}
          onChange={(e) => props.onQueryChange(e.target.value)}
          placeholder="Например, Дуб Сонома"
          aria-label="Название декора"
          className={[
            'w-full min-h-tap rounded-lg bg-card border-[1.5px] border-accent text-ink',
            'text-[18px] px-4 py-3 placeholder:text-muted',
            'focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none',
          ].join(' ')}
        />
      </section>

      {/* Результаты — switch on state. Скролл живёт здесь (P-046). */}
      <section className="flex-1 p-4 overflow-y-auto">
        <StateView {...props} />
      </section>

      <BottomBar />
    </main>
  );
}

function StateView(props: DecorsSearchViewProps) {
  switch (props.state) {
    case 'idle':
      return <IdleState />;
    case 'loading':
      return <LoadingState />;
    case 'success':
      return (
        <div className="flex flex-col gap-5">
          <p className="text-muted text-base m-0">Найдено: {props.items?.length ?? 0}</p>
          <div className="flex flex-col gap-5">
            {(props.items ?? []).map((item) => (
              <DecorResultCard
                key={item.globalId}
                item={item}
                dominantLab={item.dominantLab}
                imageUrl={
                  item.primaryPhotoKey ? (props.imageUrls?.[item.primaryPhotoKey] ?? null) : null
                }
                onItemClick={props.onItemClick}
              />
            ))}
          </div>
          {props.nextCursor !== null && (
            <button
              type="button"
              onClick={props.onLoadMore}
              aria-label="Показать ещё"
              className={[
                'w-full min-h-tap rounded-lg bg-card border-[1.5px] border-accent text-ink',
                'text-[16px] py-3',
                'focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none active:opacity-90',
              ].join(' ')}
            >
              Показать ещё
            </button>
          )}
        </div>
      );
    case 'empty':
      return <EmptyState />;
    case 'error':
      return <ErrorState message={props.error?.message ?? ''} />;
  }
}

function IdleState() {
  return (
    <div className="flex flex-col items-center justify-center min-h-[40vh] gap-3 text-center">
      <p className="text-muted text-[18px] max-w-[280px] m-0 leading-normal">
        Введите название декора
      </p>
    </div>
  );
}

function LoadingState() {
  return (
    <div className="flex flex-col items-center justify-center min-h-[40vh] gap-3 text-center">
      <p className="text-muted text-[18px] m-0">Загружаем…</p>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="flex flex-col items-center justify-center min-h-[40vh] gap-3 text-center">
      <p className="text-muted text-[18px] max-w-[280px] m-0 leading-normal">
        Ничего не нашли. Попробуйте изменить запрос
      </p>
    </div>
  );
}

function ErrorState({ message }: { message: string }) {
  return (
    <div className="flex flex-col items-center justify-center min-h-[40vh] gap-3 text-center">
      <p className="text-error text-[18px] max-w-[280px] m-0 leading-normal">
        Не удалось получить ответ. Проверьте интернет и попробуйте снова.
      </p>
      {message && <p className="text-muted text-sm">{message}</p>}
    </div>
  );
}
